import { CdkDragEnd, CdkDragMove, CdkDragStart } from "@angular/cdk/drag-drop";
import { Shift, Table } from "./reservation";
import { ReservationTag } from "./reservation-tag";

export type ConnectionEdge = 'Top' | 'Right' | 'Bottom' | 'Left';

export type TableImageType = 'square2' | 'square4' 
    | 'rect2' | 'rect4' | 'rect6' | 'rect8' | 'rect10' 
    | 'round2' | 'round4' | 'round6' | 'round8' | 'round10'
    | 'seabed';

export type TableRotation = 360 | 90 | 45 | 315;

export type WorkingDateType = 'regular' | 'special';

export type TypeOfModification = 'CREATED' | 'EDITED' | 'DELETED';

export interface OnlineReservationCategory {
    id?: string;
    photo?: string;
    name?: string;
    description?: string | null;
    terms?: string | null;
    menuLink?: string;
    hidden?: boolean;
    iconColor?: string;
    iconCaption?: string;
    displayOrder?: number;
    timeperformed?: any;
    seasons?: Season[];
    subCategories?: OnlineReservationCategory[];

    nameLocalized?: string;
}

export interface Season {
    id: string;
    photo: string;
    name: string;
    description: string;
    terms: string;
    menuLink: string;
    hidden: boolean;
    from: string;
    to: string;
    chargeFee: number;
    chargeType: string;
    timeperformed: any;
}

export interface WorkingDate {
    workingDateId: string;
    restaurantId: string;
    workingDate: string;
    typeOfWorkingDate: WorkingDateType;
    version?: number;
    isCustom: boolean;
    timeperformed?: number;
}

export interface Section {
    sectionId: string;
    workingDateId: string;
    sectionName: string;
    sectionOrder: number;
    sectionCapacity: number;
    sectionTables: number | null;
    version?: number;
    excludeOnline?: boolean;
    timeperformed?: number;
}

export interface BatchSynchRequestUpdated {
    restaurantId: string;
    syncedBatchReadRecords: number[]; // EMPTY
    affectedSettings: BatchAffectedSetting[]; // EMPTY
    affectedCategories: BatchAffectedCategory[]; // EMPTY
    affectedWorkingDates: BatchAffectedWorkingDate[];
    affectedSections: BatchAffectedSection[];
    affectedTables: BatchAffectedTable[];
    affectedCustomers: BatchAffectedCustomer[]; // EMPTY
    affectedSources: BatchAffectedSource[]; // EMPTY
    affectedReservations: BatchAffectedReservation[]; // EMPTY
    hasOrderNumber: boolean; // THE NUMBER OF THE USER ACTION TO EXECUTE WITH THE SAME ORDER
}

export interface BatchAffectedSetting {
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    pendingSyncOrder: number;
    orderNumber: number;
    settings: SettingsDetails;
    shifts: ShiftDetails[];
}

export interface BatchAffectedCategory {
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    orderNumber: number;
    categoryDetails: CategoryDetails;
    pendingSyncOrder: number;
}

export interface BatchAffectedWorkingDate {
    isConflict: boolean; // DEFAULT FALSE - NOT USED
    modifiedBy: string; // SURNAME OF USER
    typeOfModification: TypeOfModification; // CREATED - EDITED - DELETED
    timePerformed: number; // LONG
    workingDateDetails: WorkingDateDetails; 
    orderNumber: number; // 0 - NOT USED
    pendingSyncOrder: number; // THE NUMBER OF THE USER ACTION TO EXECUTE WITH THE SAME ORDER (1,2,3,4....CAN BE INITIALIZED AT LOG IN)
}

export interface BatchAffectedSection {
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    sectionDetails: SectionDetails;
    orderNumber: number;
    pendingSyncOrder: number;
}

export interface BatchAffectedTable {
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    tableDetails: TableDetails;
    orderNumber: number;
    pendingSyncOrder: number;
}

export interface BatchAffectedCustomer {
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    customerDetails: CustomerDetails;
    orderNumber: number;
    pendingSyncOrder: number;
}

export interface BatchAffectedSource {
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    sourceDetails: SourceDetails;
    orderNumber: number;
    pendingSyncOrder: number;
}

export interface BatchAffectedReservation {
    sourceTypeOfVisit: string;
    sourceCategory: string;
    sourceType: string;
    isConflict: boolean;
    modifiedBy: string;
    typeOfModification: TypeOfModification;
    timePerformed: number;
    orderNumber: number;
    reservationDetails: ReservationDetails;
    customerDetails: CustomerDetails;
    sourceDetails: SourceDetails;
    pendingSyncOrder: number;
}

export interface SettingsDetails {
    settingsId: string;
    oneToTwoCoversDuration: number;
    threeToFourCoversDuration: number;
    fiveOrMoreCoversDuration: number;
    arrivalTimeAlarmNotification: number;
    smsSender: string;
    smsDefaultText: string;
    smsDefaultCountryCode: string;
    timeperformed: number;
    onlineReservatioSettings: OnlineReservationsSettings;
}

export interface ShiftDetails {
    shiftId: string;
    title: string; // can be one of Morning, Afternoon, Night, Midnight (case sensitive)
    fromHour: number;
    fromMin: number;
    toHour: number;
    toMin: number;
    type: number; // 0 = morning, 1 = afternoon, 2 = night, 3 = midnight
    isActive: boolean;
    settingsId: string;
    timeperformed: number;
}

export interface CategoryDetails {
    name: string;
    type: string;
    version: number;
    timeperformed: number;
    categoryId: string; //uniqueId = restaurant_Id()+loggedInEmployee_Id+nanotime; System.nanotime;
}

export interface WorkingDateDetails {
    workingDateId: string;
    restaurantId: string;
    workingDate: string;
    typeOfWorkingDate: WorkingDateType; // 'regular' | 'special'
    version: number;
    timeperformed?: number;
    isCustom: boolean;
}

export interface SectionDetails {
    sectionId: string;
    workingDateId: string;
    sectionName: string;
    sectionOrder: number;
    sectionCapacity: number;
    version: number;
    timeperformed?: number;
    excludeOnline?: boolean;
}

export interface TableDetails {
    tableId: string;
    sectionId: string;
    workingDateId: string;
    tableName: string;
    minCapacity: number;
    maxCapacity: number;
    tableOrder: number;
    version: number;
    timeperformed?: number;
    x: number;
    y: number;
    rotation: number;
    imgType: string; // Can be: round2,square2,round4,square4,rect4,round6,rect6,round8,rect8,round10,rect10
    excludeOnline?: boolean;
    excludeOnlineThirdParty?: boolean;
    onlineCategory?: OnlineReservationCategory | null;
    joinTablesIds?: string;
}

export interface CustomerDetails {
    customerId: string;
    surname: string;
    name: string;
    customerType: string; // the displayed value Type (Simple, Regular etc)
    mail: string;
    allergy: string;
    dislike: string;
    like: string;
    smokingArea: string;
    notes: string;
    spender: string;
    telephone: string;
    homePhone: string;
    officePhone: string;
    otherPhone: string;
    favouriteTables: string;
    last_visit: number;
    before_last_visit: number;
    reservations_counter: number;
    cancelled_counter: number;
    notshown_counter: number;
    rejected_counter: number;
    officeMail: string;
    otherMail: string;
    birthDay: string; // for example: 23 July/ To be defined that this may be a date than string
    group_of_cust: CategoryDetails;
    cust_job: CategoryDetails;
    cust_company: CategoryDetails;
    company_vat: string;
    tourist: boolean;
    hasBaby: boolean;
    married: boolean;
    nationality: string;
    country: string;
    city: string;
    region: string;
    address: string;
    zipcode: string;
    gender: string;
    consentStatus: number; // = 0 or 1 or 2
    consentSignature: Int8Array[];
    consentLog: string;
    customerTags: ReservationTag[];
    // this is the time where the user created the customer from the Reservation
    // or the Customer Form
    // the value is in long format. Example How to find and set the proper time.
    // Date now = new Date();
    // Timestamp currentTime = new Timestamp(now.getTime());
    // createdCustomer.getMyPreferences().setCreationTime(currentTime.getTime());
    creationTime: number;
}

export interface SourceDetails {
    id: string;
    typeOfVisit: string;
    sourceType: string;
    sourceCategory: string;
    companyTitle: string;
    contactSurname: string;
    contactName: string;
    contactTelephone: string;
    contactMail: string;
    timeperformed: number;
}

export interface ReservationDetails {
    reservationId: string;
    reservationsStatus: string;
    userId: string;
    arrivalTime: string;
    covers: number;
    purpose: string;
    historyChecked: boolean;
    customField1: number;
    allocatedTables: string;
    onlineCategory: OnlineReservationCategory;
    roomNumber: string;
    reservationType: string;
    returnTime: string;
    allergy: string;
    childrerChairs: number;
    event: string;
    channel: string;
    dislike: string;
    like: string;
    fixMenu: boolean;
    selectedMenu: string;
    smokingArea: boolean;
    paymentType: string;
    notes: string;
    post: boolean;
    positiveComment: boolean;
    negativeComment: boolean;
    customerComment: string;
    createdBy: string;
    confirmedBy: string;
    expiredArrivalTime: boolean;
    expiredReturnTime: boolean;
    bill: number;
    deposit: boolean;
    tokenization: boolean;
    walkIn: boolean;
    lateBooking: boolean;
    creationTime: number;
    discount: number;
    hasCake: boolean;
    duration: number;
    online: boolean;
    availabilityId: string;
    timerStartedAt: number;
    onlinePaymentBillable: boolean;
    timePerformed: string;
    orderBody: any; //RequestIntegrationSunsoftInsertNewBillInfo, MANDATORY in case of integration with POS
    reservationTags: ReservationTag[];
}

export interface OnlineReservationsSettings {
    typeOfOnlineAvailability: number;
    validFrom: string; //"2019-11-01 10:00:00.00"
    validTo: string; //"2020-04-01 10:00:00.00"
    totalCoversByInterval: number;
    totalCapacity: number;
    realTimeAvailability: boolean;
    assignTable: boolean;
    typeOfOnlinePayment: string; // 'DEPOSIT','PICKUP', 'TOKENIZATION'
}

export interface TablesPlanUpdateResponse {    
    Status: boolean;
    ErrorCode?: any;
    ErrorDescription?: any;
}

export interface SettingsDataResponse extends TablesPlanUpdateResponse {
    settings: Settings;
    shifts: Shift[];
}

export interface SettingsDataRequest {
    settings: Settings;
    shifts: Shift[];
    orderNumber: number;
    pendingSyncOrder: number;
    timePerformed: string;
    typeOfModification: string;
    isConflict: boolean;
    modifiedBy: string;
}

export interface Settings {
    settingsId: string;
    oneToTwoCoversDuration: number;
    threeToFourCoversDuration: number;
    fiveOrMoreCoversDuration: number;
    smsSender: string;
    smsDefaultText: string;
    "smsDefaultCountryCode ": string;
    timeperformed: number;
    arrivalTimeAlarmNotification: number;
    attendanceRate: number;
    onlineReservatioSettings: any;
}

export type DragTableHelper = {
    left: number;
    top: number;
    isVertical: boolean;
}

export type ClickEvent = {
    event: MouseEvent;
    table: Table;
}

export type DragStartEvent = {
    event: CdkDragStart;
    table: Table;
}

export type DragMoveEvent = {
    event: CdkDragMove;
    table: Table;
}

export type DragEndEvent = {
    event: CdkDragEnd;
    table: Table;
}

export type TableConnection = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export enum ReservationType {
    Booked,
    Seated,
    Completed,
    Waiting,
    Pending
}

export type TablesMode = 'Tables' | 'JoinedTables';
export type ExcluceFromOnlineChannelsMode = '' | 'Own' | 'ThirdParty';

export type CountryCode = {
    country: string;
    code: string;
}

